import React, { useEffect, useState } from 'react';
import { WithStyles } from '@material-ui/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Spinner from '@material-ui/core/CircularProgress';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import { ArrowLeft } from '@material-ui/icons';
import SearchBar from '../SearchBar';
import TablePagination from '../../components/CustomTablePagination';
import Table from '../../components/CustomTable';
import { ROWS_PER_PAGE_OPTIONS } from '../../constants/table';

import styles from './styles';
import { HeaderRowConfig } from '../../components/CustomTable/CustomTable';
import { Order } from '../../utils/table';
import { Query } from '../../utils/query';
import { SetFilterPayload } from '../../redux/types/filter';
import usePropertyVisitsReport from './hooks/usePropertyVisitsReport';
import { PropertyVisitReport } from '../../redux/types/properties';
import { ExportAllParams } from '../../redux/types/exportAll';
import { MimeType } from '../../redux/types/mime';

type Props = {
  loading: boolean;
  exportAllLoading: boolean;
  propertyVisitsReport: any[];
  propertyVisitsReportTotal: number;
  loadPropertyTourReport: (query?: Query) => void;
  setFilterState: (payload: SetFilterPayload) => void;

  exportPropertyVisitsReport: (
    selection: Array<number | string>,
    startDate: Date,
    endDate: Date
  ) => void;
  exportAllPropertyVisitsReport: (
    path: string,
    mimeType: MimeType,
    params: ExportAllParams,
    query?: Query
  ) => void;
  printPropertyVisitsReport: (
    selection: Array<number | string>,
    startDate: Date,
    endDate: Date
  ) => void;

} & WithStyles<typeof styles> &
RouteComponentProps;

const searchBarPlaceholder: string = 'Search all visits report';

const headRows: HeaderRowConfig<PropertyVisitReport>[] = [
  {
    key: 'community_name',
    align: 'center',
    disablePadding: false,
    label: 'Community',
  },
  {
    key: 'address_1',
    align: 'center',
    disablePadding: false,
    label: 'Full addreess',
  },
  {
    key: 'lot_num',
    align: 'center',
    disablePadding: false,
    label: 'Lot number',
  },
  {
    key: 'marketing_name',
    align: 'center',
    disablePadding: false,
    label: 'Marketing name',
  },
  {
    key: 'no_of_tours',
    align: 'center',
    disablePadding: false,
    label: '# of tours',
  },
  {
    key: 'avg_tours_per_day',
    align: 'center',
    disablePadding: false,
    label: 'Avg # of tours per day',
  },
  {
    key: 'no_of_repeat_visitors',
    align: 'center',
    disablePadding: false,
    label: '# of repeat visitors',
  },

  {
    key: 'days_since_activation',
    align: 'center',
    disablePadding: false,
    label: 'Days since activation',
  },
  {
    key: 'days_since_last_tour',
    align: 'center',
    disablePadding: false,
    label: 'Days since last visit',
  },
  {
    key: 'top_toured_day',
    align: 'center',
    disablePadding: false,
    label: 'Top Tour Day of Week',
  },
  {
    key: 'top_toured_hour',
    align: 'center',
    disablePadding: false,
    label: 'Top Tour Time',
  },
];

const ReportPropertyVisit: React.FC<Props> = (props) => {
  const {
    classes,
    propertyVisitsReport,
    propertyVisitsReportTotal,
    loadPropertyTourReport,
    loading,
    history,
    exportAllLoading,
    exportAllPropertyVisitsReport,
    exportPropertyVisitsReport,
    printPropertyVisitsReport,
    setFilterState,
  } = props;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const {
    order,
    orderBy,
    page,
    search,
    rowsPerPage,
    builderId,
    startDate,
    endDate,
    setSearch,
    setOrder,
    setOrderBy,
    setBuilderId,
    setStartDate,
    setEndDate,
    setPage,
    setRowsPerPage,
    dispatchVisitsReport,
  } = usePropertyVisitsReport(loadPropertyTourReport);

  const [selected, setSelected] = useState<PropertyVisitReport['community_name'][]>([]);
  const selectedLength = selected.length;

  useEffect(() => {
    setSelected([]);
  }, [setSelected, page, rowsPerPage]);

  useEffect(
    () => () => {
      const pageNameIndex = 1;
      const pathElements = history.location.pathname.split('/');
      const pathname = pathElements[pageNameIndex];

      if (pathElements.length === 3) {
        return;
      }

      if (pathname !== 'reportPropertyTours') {
        dispatchVisitsReport('RESET_STATE');
      }
    },
    [dispatchVisitsReport, history.location.pathname],
  );

  useEffect(() => {
    if (!startDate || !endDate) {
      const newBuilderId = searchParams.get('builderId') ?? '';
      const newStartDate = new Date(searchParams.get('startDate') ?? '');
      const newEndDate = new Date(searchParams.get('endDate') ?? '');
      setBuilderId(newBuilderId);
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setFilterState({
        type: 'reports',
        name: 'builderId',
        value: newBuilderId,
      });

      setFilterState({
        type: 'reports',
        name: 'startDate',
        value: newStartDate,
      });

      setFilterState({
        type: 'reports',
        name: 'endDate',
        value: newEndDate,
      });
    }
  }, [
    builderId,
    startDate,
    endDate,
    searchParams,
    setBuilderId,
    setEndDate,
    setStartDate,
    setFilterState,
  ]);

  const handleSelect = (newSelected: any[]) => {
    setSelected(newSelected);
  };

  const exportAll = () => {
    exportAllPropertyVisitsReport(
      'properties/tourReport',
      'text/csv',
      {},
      {
        startDate, endDate, limit: 25, page,
      },
    );
  };

  const handleExport = () => {
    if (selectedLength !== 0 && startDate && endDate) {
      exportPropertyVisitsReport(selected, startDate, endDate);
    }
  };

  const handlePrint = () => {
    if (selectedLength !== 0 && startDate && endDate) {
      printPropertyVisitsReport(selected, startDate, endDate);
    }
  };

  const handleChangeOrder = (
    newOrder: Order,
    newOrderBy: keyof PropertyVisitReport,
  ) => {
    setOrder(newOrder);
    setOrderBy(newOrderBy);
    setPage(0);
    dispatchVisitsReport('CHANGE_VISITS_REPORT', {
      currentOrder: newOrder,
      currentOrderBy: newOrderBy,
      currentPage: 0,
    });
  };

  const handleChangePage = (pageNumber: number) => {
    setPage(pageNumber);
    dispatchVisitsReport('CHANGE_VISITS_REPORT', { currentPage: pageNumber });
  };

  const handleChangeRowsPerPage = (rowsPerPageAmount: number) => {
    setPage(0);
    setRowsPerPage(rowsPerPageAmount);
    dispatchVisitsReport('CHANGE_VISITS_REPORT', {
      currentPage: 0,
      rowsPerPageAmount,
    });
  };

  const handleSearch = (value: string): void => {
    setSearch(value);
    setPage(0);
    dispatchVisitsReport('CHANGE_VISITS_REPORT', {
      currentPage: 0,
      contextSearch: value,
    });
  };

  return (
    <>
      <Grid container justify="space-between" style={{ marginBottom: 25 }}>
        <Grid item>
          <Button variant="contained" onClick={() => history.push('/reports')}>
            <ArrowLeft />
            GoBack
          </Button>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs>
          <SearchBar
            defaultValue={search}
            placeholder={searchBarPlaceholder}
            onChange={handleSearch}
          />
        </Grid>
      </Grid>
      <div className={classes.contentWrapper}>
        <Toolbar>
          <div>
            <Typography variant="h5">
              Property Tour Report
              {selectedLength > 0 ? ` ${selectedLength} selected.` : ''}
            </Typography>
          </div>
          <div className={classes.spacer} />
          {selectedLength > 0 && (
          <div className={classes.toolbarActions}>
            <span>Selected actions: </span>
            <Button
              id="fs-export-visit"
              size="small"
              disabled={selectedLength === 0}
              onClick={handleExport}
            >
                Export
            </Button>
            <Button
              size="small"
              disabled={selectedLength === 0}
              onClick={handlePrint}
            >
                Print
            </Button>
          </div>
          )}
          {exportAllLoading ? (
            <Spinner size={20} />
          ) : (
            <Button
              id="fs-export-visits"
              size="small"
              onClick={exportAll}
              disabled={propertyVisitsReport?.length === 0}
            >
              Export All
            </Button>
          )}
        </Toolbar>
        <div className={classes.tableWrapper}>
          {loading && (
            <div className={classes.spinnerWrapper}>
              <Spinner />
            </div>
          )}
          {propertyVisitsReport?.length > 0 && (
          <Table<PropertyVisitReport>
            className={classes.table}
            IDKey="property_id"
            selected={selected}
            data={propertyVisitsReport}
            rowsPerPage={rowsPerPage}
            order={order}
            orderBy={orderBy}
            header={headRows}
            onSelect={handleSelect}
            onChangeOrder={handleChangeOrder}
          />
)}
        </div>
        {propertyVisitsReport?.length > 0 && (
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          rowsPerPage={rowsPerPage}
          currentPage={page}
          rowsCount={propertyVisitsReport.length}
          rowsTotal={propertyVisitsReportTotal}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
)}
      </div>
    </>
  );
};

export default React.memo(ReportPropertyVisit);
